import EmailIcon from '@mui/icons-material/Email';
import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Training } from './EmployeeTable';

export interface PendingEmailItem {
  name: string;
  email: string;
  trainingName: string;
  applicationId: string;
}

interface Props {
  open: boolean;
  trainings: Training[];
  selectedApplicationIds: Set<string>;
  onSelectionChange: (ids: Set<string>) => void;
  pendingItems: PendingEmailItem[];
  onClose: () => void;
  onConfirm: () => void;
  sending: boolean;
}

export function SendAllModal({ open, trainings, selectedApplicationIds, onSelectionChange, pendingItems, onClose, onConfirm, sending }: Props) {
  const { t } = useTranslation();

  const filteredItems = pendingItems.filter((item) => selectedApplicationIds.has(item.applicationId));
  const allSelected = trainings.every((tr) => selectedApplicationIds.has(tr.applicationId));

  const toggleTraining = (applicationId: string) => {
    const next = new Set(selectedApplicationIds);
    if (next.has(applicationId)) {
      next.delete(applicationId);
    } else {
      next.add(applicationId);
    }
    onSelectionChange(next);
  };

  const toggleAll = () => {
    onSelectionChange(allSelected ? new Set() : new Set(trainings.map((tr) => tr.applicationId)));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 2, maxHeight: '80vh', display: 'flex', flexDirection: 'column' } }}
    >
      <DialogTitle sx={{ pt: 3, pb: 1.5, px: 3, flexShrink: 0 }}>
        {t('dashboard.training.employee.table.dialogs.send_all.title', 'E-Mails versenden')}
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 2, display: 'flex', flexDirection: 'column', overflow: 'hidden', gap: 2 }}>
        {/* Training selection */}
        {trainings.length > 0 && (
          <div className="flex-shrink-0">
            <p className="text-sm font-medium mb-2">
              {t('dashboard.training.employee.table.dialogs.send_all.training_select', 'Schulungen auswählen:')}
            </p>
            <div className="flex flex-col gap-0.5">
              <FormControlLabel
                control={<Checkbox size="small" checked={allSelected} indeterminate={!allSelected && selectedApplicationIds.size > 0} onChange={toggleAll} />}
                label={<span className="text-sm font-medium">{t('common.select_all', 'Alle')}</span>}
              />
              {trainings.map((tr) => (
                <FormControlLabel
                  key={tr.applicationId}
                  control={<Checkbox size="small" checked={selectedApplicationIds.has(tr.applicationId)} onChange={() => toggleTraining(tr.applicationId)} />}
                  label={<span className="text-sm">{tr.name}</span>}
                  sx={{ ml: 2 }}
                />
              ))}
            </div>
          </div>
        )}

        {/* Pending items list — scrollable */}
        <div className="flex flex-col min-h-0 flex-1">
          {filteredItems.length === 0 ? (
            <p className="text-sm text-gray-500">
              {t('dashboard.training.employee.table.dialogs.send_all.empty', 'Keine ausstehenden E-Mails zu versenden.')}
            </p>
          ) : (
            <>
              <p className="text-sm text-gray-500 mb-2 flex-shrink-0">
                {t(
                  'dashboard.training.employee.table.dialogs.send_all.description',
                  '{{count}} E-Mail(s) werden versendet:',
                  { count: filteredItems.length }
                )}
              </p>
              <div className="overflow-y-auto flex-1 min-h-0 pr-1 space-y-3 text-sm">
                {Object.entries(
                  filteredItems.reduce<Record<string, PendingEmailItem[]>>((acc, item) => {
                    (acc[item.trainingName] ??= []).push(item);
                    return acc;
                  }, {})
                ).map(([trainingName, items]) => (
                  <div key={trainingName}>
                    <p className="font-medium text-gray-700 mb-1">
                      {trainingName} <span className="text-gray-400 font-normal">({items.length})</span>
                    </p>
                    <ul className="space-y-0.5 pl-3">
                      {items.map((item, i) => (
                        <li key={i} className="flex gap-2 items-baseline text-gray-500">
                          <span className="truncate">{item.name}</span>
                          <span className="text-gray-400 truncate shrink-0">({item.email})</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, flexShrink: 0 }}>
        <Button onClick={onClose} disabled={sending}>
          {t('components.ui.alert_dialog.actions.cancel', 'Abbrechen')}
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={sending || filteredItems.length === 0}
          startIcon={sending ? <CircularProgress size={14} /> : <EmailIcon />}
        >
          {t('dashboard.training.employee.table.dialogs.send_all.confirm', 'Jetzt senden')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
